import React from 'react';
import './PricingSection.css';
import { trackPricingInterest, trackCTAClick } from '../analytics';

const PricingSection = ({ onGetStarted }) => {
    return (
        <section id="pricing" className="pricing-section">
            <div className="pricing-container">
                {/* Section Header */}
                <header className="pricing-header">
                    <h2 className="pricing-headline">
                        Infrastructure that scales with your institution
                    </h2>
                    <p className="pricing-subheadline">
                        Every institution gets the full platform. Plans determine how much your institution can grow.
                    </p>
                </header>

                {/* Plan Cards */}
                <div className="pricing-cards-container">
                    {/* Trial Card */}
                    <div className="pricing-card pricing-card-starter">
                        <h3 className="pricing-card-name">Trial</h3>
                        <p className="pricing-card-price">Free</p>
                        <p className="pricing-card-tagline">Perfect for institutions getting started.</p>
                        <div className="pricing-card-divider"></div>
                        <div className="pricing-card-features">
                            <p className="pricing-feature">Full fundraising platform access</p>
                            <p className="pricing-feature">Limited campaign creation capacity</p>
                            <p className="pricing-feature">Limited fundraising capacity</p>
                            <p className="pricing-feature">Community support</p>
                        </div>
                        <button className="pricing-card-cta pricing-cta-outline" onClick={() => { trackPricingInterest('trial'); onGetStarted('Trial'); }}>Get Started</button>
                    </div>

                    {/* Standard Card (Hero - BLACK) */}
                    <div className="pricing-card pricing-card-professional">
                        <div className="pricing-card-label">MOST POPULAR</div>
                        <h3 className="pricing-card-name">Standard</h3>
                        <p className="pricing-card-price">Custom Pricing</p>
                        <p className="pricing-card-tagline">Built for growing institutions.</p>
                        <div className="pricing-card-divider"></div>
                        <div className="pricing-card-features">
                            <p className="pricing-feature pricing-feature-header">Everything in Trial, plus:</p>
                            <p className="pricing-feature">Higher campaign creation limits</p>
                            <p className="pricing-feature">Higher fundraising capacity</p>
                            <p className="pricing-feature">Priority support</p>
                        </div>
                        <button className="pricing-card-cta pricing-cta-black" onClick={() => { trackPricingInterest('standard'); onGetStarted('Standard'); }}>Get Started</button>
                    </div>

                    {/* Premium Card */}
                    <div className="pricing-card pricing-card-premium">
                        <h3 className="pricing-card-name">Premium</h3>
                        <p className="pricing-card-price">Custom Pricing</p>
                        <p className="pricing-card-tagline">For institutions operating at scale.</p>
                        <div className="pricing-card-divider"></div>
                        <div className="pricing-card-features">
                            <p className="pricing-feature pricing-feature-header">Everything in Standard, plus:</p>
                            <p className="pricing-feature">High campaign capacity</p>
                            <p className="pricing-feature">Large fundraising limits</p>
                            <p className="pricing-feature">Dedicated support</p>
                            <p className="pricing-feature">Advanced administrative controls</p>
                        </div>
                        <button className="pricing-card-cta pricing-cta-black-alt" onClick={() => { trackPricingInterest('premium'); onGetStarted('Premium'); }}>Get Started</button>
                    </div>
                </div>

                {/* Enterprise Banner */}
                <div className="pricing-enterprise-banner">
                    <div className="pricing-enterprise-content">
                        <h3 className="pricing-enterprise-name">Enterprise</h3>
                        <p className="pricing-enterprise-description">
                            Custom infrastructure and dedicated support for institutions managing large-scale fundraising operations.
                        </p>
                        <p className="pricing-enterprise-features">
                            Custom campaign limits • Custom fundraising capacity • Dedicated infrastructure • SLA guarantees • Priority onboarding • Account manager
                        </p>
                        <button className="pricing-enterprise-cta" onClick={() => { trackPricingInterest('enterprise'); trackCTAClick('enterprise_talk_to_team'); onGetStarted('Enterprise'); }}>Talk to Our Team</button>
                    </div>
                </div>

                {/* Comparison Table */}
                <div className="pricing-comparison-section">
                    <div className="pricing-comparison-header">
                        <h3 className="pricing-comparison-title">Compare Capabilities</h3>
                        <p className="pricing-comparison-subtitle">All plans include the full platform. Limits apply only to growth capacity.</p>
                    </div>

                    <div className="pricing-table-container">
                        <table className="pricing-table">
                            <thead>
                                <tr>
                                    <th className="pricing-table-header pricing-table-header-feature">Features</th>
                                    <th className="pricing-table-header">Trial</th>
                                    <th className="pricing-table-header">Standard</th>
                                    <th className="pricing-table-header">Premium</th>
                                    <th className="pricing-table-header">Enterprise</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="pricing-table-row">
                                    <td className="pricing-table-cell pricing-table-cell-feature">Platform Access</td>
                                    <td className="pricing-table-cell">Full platform</td>
                                    <td className="pricing-table-cell">Full platform</td>
                                    <td className="pricing-table-cell">Full platform</td>
                                    <td className="pricing-table-cell">Full platform</td>
                                </tr>
                                <tr className="pricing-table-row pricing-table-row-alt">
                                    <td className="pricing-table-cell pricing-table-cell-feature">Campaign Creation Capacity</td>
                                    <td className="pricing-table-cell">Limited</td>
                                    <td className="pricing-table-cell">Higher limit</td>
                                    <td className="pricing-table-cell">High capacity</td>
                                    <td className="pricing-table-cell">Custom</td>
                                </tr>
                                <tr className="pricing-table-row">
                                    <td className="pricing-table-cell pricing-table-cell-feature">Fundraising Capacity</td>
                                    <td className="pricing-table-cell">Limited</td>
                                    <td className="pricing-table-cell">Higher capacity</td>
                                    <td className="pricing-table-cell">Large-scale fundraising</td>
                                    <td className="pricing-table-cell">Custom</td>
                                </tr>
                                <tr className="pricing-table-row pricing-table-row-alt">
                                    <td className="pricing-table-cell pricing-table-cell-feature">Active Campaign Management</td>
                                    <td className="pricing-table-cell">Included</td>
                                    <td className="pricing-table-cell">Included</td>
                                    <td className="pricing-table-cell">Included</td>
                                    <td className="pricing-table-cell">Included</td>
                                </tr>
                                <tr className="pricing-table-row">
                                    <td className="pricing-table-cell pricing-table-cell-feature">Contribution Processing</td>
                                    <td className="pricing-table-cell">Available within fundraising capacity</td>
                                    <td className="pricing-table-cell">Higher fundraising capacity</td>
                                    <td className="pricing-table-cell">Large-scale fundraising</td>
                                    <td className="pricing-table-cell">Custom</td>
                                </tr>
                                <tr className="pricing-table-row pricing-table-row-alt">
                                    <td className="pricing-table-cell pricing-table-cell-feature">Institution Scale</td>
                                    <td className="pricing-table-cell">Early-stage institutions</td>
                                    <td className="pricing-table-cell">Growing institutions</td>
                                    <td className="pricing-table-cell">Large institutions</td>
                                    <td className="pricing-table-cell">Multi-campus organizations</td>
                                </tr>
                                <tr className="pricing-table-row">
                                    <td className="pricing-table-cell pricing-table-cell-feature">Customization</td>
                                    <td className="pricing-table-cell">-</td>
                                    <td className="pricing-table-cell">-</td>
                                    <td className="pricing-table-cell">-</td>
                                    <td className="pricing-table-cell">Custom</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footnote */}
                <div className="pricing-disclaimer">
                    <p>All plans include access to the full fundraising platform. Limits apply only to campaign creation and fundraising capacity.</p>
                    <p>Contributions and existing campaigns remain active regardless of plan limits.</p>
                </div>
            </div>
        </section>
    );
};

export default PricingSection;
